import React, { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Button } from '@actual-app/components/button';
import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import {
  SvgAdd,
  SvgCheveronRight,
  SvgClose,
} from '@actual-app/components/icons/v1';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import {
  addDays,
  nextPayday,
  resolveDeadline,
} from '@actual-app/core/shared/mtxbudget';
import type { DeadlineKind } from '@actual-app/core/shared/mtxbudget';
import {} from '@actual-app/core/shared/mtxbudget-parcours';
import type {
  WalkInputs,
  WalkStep,
} from '@actual-app/core/shared/mtxbudget-parcours';
import { q } from '@actual-app/core/shared/query';
import { v4 as uuidv4 } from 'uuid';

import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { useAccounts } from '#hooks/useAccounts';
import { usePayees } from '#hooks/usePayees';
import { useSchedules } from '#hooks/useSchedules';

import { solveTask } from './solver';
import {
  longDate,
  MtxPage,
  Section,
  shortDate,
  textDate,
  useMoney,
} from './ui';
import { useMtxbudget } from './useMtxbudget';
import { useParcours } from './useParcours';

type Kind = 'depense' | 'entree' | 'investissement';
const KINDS: Kind[] = ['depense', 'entree', 'investissement'];

/** How the date of an investment is given. */
type When =
  | 'week'
  | 'month'
  | 'in2'
  | 'in3'
  | 'year'
  | 'date'
  | 'period'
  | 'none';
const WHENS: When[] = [
  'week',
  'month',
  'in2',
  'in3',
  'year',
  'date',
  'period',
  'none',
];

/** Something the schedules do not know yet, whatever it is kept as. */
type Item = {
  kind: Kind;
  /** Schedule id of an expense, id of an income or of an investment. */
  id: string;
  name: string;
  /** Cents, always positive. */
  amount: number;
  /** 'YYYY-MM-DD', or '' for an investment without a date. */
  date: string;
  /** Investments only: start of the wished period, how the date was given,
   *  and whether it is paid before the debts. */
  from: string;
  when: When;
  mandatory: boolean;
};

type Draft = Omit<Item, 'id' | 'amount'> & {
  mode: 'add' | 'edit';
  id: string | null;
  amount: string;
  /** True once saving was tried: the missing fields are then pointed out. */
  tried: boolean;
};

type Impact = { label?: string; text: string; color: string };

function hours(value: number) {
  return `${value.toLocaleString('fr-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
}

/** Cents typed as « 1 250,50 », or null when it is not a positive amount. */
function parseAmount(value: string): number | null {
  const cents = Math.round(
    Number(value.replace(/\s/g, '').replace(',', '.')) * 100,
  );
  return Number.isFinite(cents) && cents > 0 ? cents : null;
}

/** The same day some months later, or the last day of that month. */
function monthsLater(date: string, months: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const last = new Date(Date.UTC(year, month + months, 0)).getUTCDate();
  return new Date(Date.UTC(year, month - 1 + months, Math.min(day, last)))
    .toISOString()
    .slice(0, 10);
}

/** The date a quick choice stands for; '' when it is typed or absent. */
function dateOf(when: When, today: string): string {
  switch (when) {
    case 'week':
      return resolveDeadline('end-of-week', today).by ?? '';
    case 'month':
      return resolveDeadline('end-of-month', today).by ?? '';
    case 'year':
      return resolveDeadline('end-of-year', today).by ?? '';
    case 'in2':
      return monthsLater(today, 2);
    case 'in3':
      return monthsLater(today, 3);
    default:
      return '';
  }
}

const DEADLINE_OF: Record<When, DeadlineKind> = {
  week: 'end-of-week',
  month: 'end-of-month',
  year: 'end-of-year',
  in2: 'date',
  in3: 'date',
  date: 'date',
  period: 'period',
  none: 'none',
};

function whenOf(deadline: DeadlineKind | undefined, wished?: string): When {
  if (!wished) {
    return 'none';
  }
  switch (deadline) {
    case 'end-of-week':
      return 'week';
    case 'end-of-month':
      return 'month';
    case 'end-of-year':
      return 'year';
    case 'period':
    case 'month':
      return 'period';
    default:
      return 'date';
  }
}

/** The walk as it would be with one item added, changed or taken out. */
function withItem(
  inputs: WalkInputs,
  account: string | undefined,
  remove: Item | null,
  add: Item | null,
): WalkInputs {
  const next: WalkInputs = {
    ...inputs,
    occurrences: inputs.occurrences.filter(
      o =>
        !(remove?.kind === 'depense' && o.scheduleId === remove.id) &&
        !(
          remove?.kind === 'investissement' &&
          o.id === `investissement/${remove.id}`
        ),
    ),
    incomes: (inputs.incomes ?? []).filter(
      i => !(remove?.kind === 'entree' && i.id === remove.id),
    ),
    needs: (inputs.needs ?? []).filter(
      n => !(remove?.kind === 'investissement' && n.id === remove.id),
    ),
  };
  // An investment paid before the debts is a charge at its date.
  const asCharge =
    add?.kind === 'depense' ||
    (add?.kind === 'investissement' && add.mandatory && add.date !== '');
  if (add && asCharge) {
    next.occurrences = [
      ...next.occurrences,
      {
        id: `draft/${add.date}`,
        name: add.name,
        date: add.date,
        amount: -add.amount,
        recurring: false,
        account: add.kind === 'depense' ? account : undefined,
      },
    ];
  } else if (add?.kind === 'entree') {
    next.incomes = [
      ...(next.incomes ?? []),
      { id: 'draft', name: add.name, amount: add.amount, date: add.date },
    ];
  } else if (add?.kind === 'investissement') {
    next.needs = [
      ...(next.needs ?? []),
      {
        id: 'draft',
        name: add.name,
        amount: add.amount,
        wished: add.date || undefined,
        priority: 999,
      },
    ];
  }
  return next;
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 13,
        color: theme.pageTextSubdued,
      }}
    >
      {label}
      {children}
      {error ? (
        <span style={{ fontSize: 12, color: theme.errorText }}>{error}</span>
      ) : (
        hint && <span style={{ fontSize: 12 }}>{hint}</span>
      )}
    </label>
  );
}

function inputStyle(invalid: boolean): CSSProperties {
  return {
    minHeight: 48,
    minWidth: 0,
    boxSizing: 'border-box',
    border: `1px solid ${invalid ? theme.errorText : theme.tableBorder}`,
    borderRadius: 4,
    backgroundColor: theme.pageBackground,
    color: theme.pageText,
    font: 'inherit',
    fontSize: 15,
    padding: '0 12px',
    colorScheme: 'dark',
  };
}

function chipStyle(isOn: boolean, background: string): CSSProperties {
  return {
    minHeight: 44,
    padding: '0 12px',
    borderRadius: 22,
    border: `1px solid ${isOn ? theme.buttonPrimaryBackground : theme.tableBorder}`,
    backgroundColor: isOn ? theme.buttonPrimaryBackground : background,
    color: isOn ? theme.buttonPrimaryText : theme.pageText,
    font: 'inherit',
    fontSize: 13,
    fontWeight: isOn ? 600 : 400,
    cursor: 'pointer',
  };
}

/**
 * One place for what the schedules do not know yet: one-off expenses, money
 * other than the salary, investments to fund for a date. Each can be added,
 * seen, changed and removed here, and says what it does to the pays.
 */
export function PrevisionsPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const { isNarrowWidth } = useResponsive();
  const { config, saveConfig, isLoading } = useMtxbudget();
  const { steps, occurrences, inputs } = useParcours();
  const { data: accounts = [] } = useAccounts();
  const { data: payees = [] } = usePayees();
  const { schedules } = useSchedules({ query: q('schedules').select('*') });
  const [params, setParams] = useSearchParams();
  const today = monthUtils.currentDay();

  const [filter, setFilter] = useState<Kind | 'tout'>('tout');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState<Impact[]>([]);
  const [confirming, setConfirming] = useState(false);
  // Why the last save or removal did not go through, shown in the sheet.
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    text: string;
    undo?: () => void;
  } | null>(null);

  const kindLabel: Record<Kind, string> = {
    depense: t('Dépense'),
    entree: t("Entrée d'argent"),
    investissement: t('Investissement'),
  };
  const rowLabel: Record<Kind, string> = {
    depense: t('Dépense ponctuelle'),
    entree: t("Entrée d'argent"),
    investissement: t('Investissement'),
  };
  const kindColor: Record<Kind, string> = {
    depense: theme.mobileNavItemSelected,
    entree: theme.noticeTextLight,
    investissement: theme.warningText,
  };
  const whenLabel: Record<When, string> = {
    week: t('Cette semaine'),
    month: t('Ce mois-ci'),
    in2: t('Dans 2 mois'),
    in3: t('Dans 3 mois'),
    year: t("Avant la fin de l'année"),
    date: t('Date précise'),
    period: t('Sur une période'),
    none: t('Sans date'),
  };

  // The account the salary arrives in pays a one-off expense.
  const account =
    config?.cashAccountIds.find(id =>
      accounts.some(a => a.id === id && !a.closed),
    ) ?? accounts.find(a => !a.closed && !a.offbudget)?.id;
  const maxHours = config?.maxHoursPerWeek ?? 45;

  function blank(kind: Kind, date?: string): Draft {
    return {
      mode: 'add',
      kind,
      id: null,
      name: '',
      amount: '',
      date: date ?? (kind === 'investissement' ? '' : today),
      from: '',
      when: kind === 'investissement' && !date ? 'none' : 'date',
      mandatory: false,
      tried: false,
    };
  }

  // Opened from another screen: « /previsions?ajouter=depense&date=… ».
  const asked = params.get('ajouter');
  const askedDate = params.get('date');
  useEffect(() => {
    if (asked && KINDS.includes(asked as Kind)) {
      setDraft(blank(asked as Kind, askedDate ?? undefined));
      setParams({}, { replace: true });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [asked, askedDate, today, setParams]);

  useEffect(() => {
    if (!notice) {
      return;
    }
    const timer = setTimeout(() => setNotice(null), 7000);
    return () => clearTimeout(timer);
  }, [notice]);

  const plain = { from: '', when: 'date' as const, mandatory: false };
  const items: Item[] = config
    ? [
        ...occurrences
          .filter(o => o.recurring === false && o.amount < 0 && o.scheduleId)
          .map(o => ({
            ...plain,
            kind: 'depense' as const,
            id: o.scheduleId as string,
            name: o.name,
            amount: -o.amount,
            date: o.date,
          })),
        ...config.extraIncomes.map(income => ({
          ...plain,
          kind: 'entree' as const,
          id: income.id,
          name: income.name,
          amount: income.amount,
          date: income.date,
        })),
        ...config.projects.map(project => ({
          kind: 'investissement' as const,
          id: project.id,
          name: project.name,
          amount: project.amount,
          date: project.wished ?? '',
          from: project.wishedFrom ?? '',
          when: whenOf(project.deadline, project.wished),
          mandatory: Boolean(project.mandatory),
        })),
      ]
    : [];

  // Opened from the calendar on one item: « /previsions?modifier=entree:<id> ».
  const wanted = params.get('modifier');
  const wantedItem = wanted
    ? items.find(item => `${item.kind}:${item.id}` === wanted)
    : undefined;
  const wantedKey = wantedItem ? wanted : null;
  useEffect(() => {
    if (wantedItem) {
      open(wantedItem);
      setParams({}, { replace: true });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey]);

  /** The pay received on or before a date; the first one for anything earlier. */
  function payBefore(date: string): string {
    if (!config) {
      return date;
    }
    const next = nextPayday(config.pay, date);
    const before = next === date ? date : addDays(next, -14);
    const first = nextPayday(config.pay, today);
    return before < first ? first : before;
  }

  function stepOf(walk: WalkStep[], date: string) {
    return walk.find(step => step.payday === payBefore(date));
  }

  function fate(item: Item): { text: string; color?: string } {
    if (item.kind === 'entree') {
      return {
        text: t('disponible le {{date}}', { date: textDate(item.date) }),
      };
    }
    // Nothing can be said of an item before the walk has been computed.
    if (steps.length === 0) {
      return { text: t('calcul en cours…') };
    }
    if (item.kind === 'investissement' && !item.mandatory) {
      const need = steps.at(-1)?.needs.find(n => n.id === item.id);
      if (!item.date) {
        return {
          text: t('sans date : financé quand les dettes le permettent'),
        };
      }
      if (!need) {
        return { text: t('au-delà des six prochains mois') };
      }
      return need.paidSoFar >= need.amount
        ? { text: t('financé pour sa date'), color: theme.noticeTextLight }
        : {
            text: t('non financé à sa date : il manque {{amount}}', {
              amount: money(need.amount - need.paidSoFar),
            }),
          };
    }
    const step = stepOf(steps, item.date);
    if (!step) {
      return { text: t('au-delà des six prochains mois') };
    }
    if (step.shortfall > 0) {
      return {
        text: t('la paie du {{date}} ne suffit pas : il manque {{amount}}', {
          date: textDate(step.payday),
          amount: money(step.shortfall),
        }),
        color: theme.errorText,
      };
    }
    return item.kind === 'investissement'
      ? {
          text: t('payé à sa date par la paie du {{date}}, avant les dettes', {
            date: textDate(step.payday),
          }),
          color: theme.noticeTextLight,
        }
      : {
          text: t('payée par la paie du {{date}}', {
            date: textDate(step.payday),
          }),
        };
  }

  const shown = items
    .filter(item => filter === 'tout' || item.kind === filter)
    .sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999'));
  const groups = [
    ...new Set(shown.map(i => (i.date ? payBefore(i.date) : ''))),
  ];

  // ── The sheet ────────────────────────────────────────────────────────────

  const editing =
    draft?.mode === 'edit'
      ? (items.find(i => i.kind === draft.kind && i.id === draft.id) ?? null)
      : null;
  const cents = draft ? parseAmount(draft.amount) : null;
  // Two schedules cannot bear the same name.
  const nameTaken =
    draft?.kind === 'depense' &&
    schedules.some(
      schedule =>
        schedule.id !== draft.id &&
        schedule.name?.trim().toLowerCase() === draft.name.trim().toLowerCase(),
    );
  const nameError = !draft
    ? null
    : draft.name.trim() === ''
      ? t("Indiquez de quoi il s'agit.")
      : nameTaken
        ? t('Une échéance porte déjà ce nom : choisissez-en un autre.')
        : null;
  const isInvestment = draft?.kind === 'investissement';
  const dateError = !draft
    ? null
    : isInvestment && draft.when === 'none'
      ? null
      : draft.date === ''
        ? t('Choisissez une date.')
        : draft.kind !== 'entree' && draft.date < today
          ? t("Cette date est passée : choisissez aujourd'hui ou plus tard.")
          : isInvestment && draft.when === 'period' && draft.from === ''
            ? t('Indiquez le début de la période.')
            : isInvestment && draft.when === 'period' && draft.from > draft.date
              ? t('Le début de la période doit précéder sa fin.')
              : null;
  const valid = Boolean(draft && cents !== null && !nameError && !dateError);

  // What saving would change, computed on the walk itself before saving.
  const draftKey = draft
    ? JSON.stringify([
        draft.kind,
        draft.id,
        draft.amount,
        draft.date,
        draft.from,
        draft.when,
        draft.mandatory,
      ])
    : '';
  useEffect(() => {
    if (!draft || !inputs || !config) {
      setImpact([]);
      return;
    }
    if (cents === null) {
      setImpact([
        {
          text: t('Indiquez un montant supérieur à zéro.'),
          color: draft.tried ? theme.errorText : theme.pageTextSubdued,
        },
      ]);
      return;
    }
    if (dateError) {
      setImpact([
        {
          text: t('Choisissez une date pour voir ce que ça change.'),
          color: theme.pageTextSubdued,
        },
      ]);
      return;
    }
    let cancelled = false;
    const item: Item = {
      kind: draft.kind,
      id: 'draft',
      name: draft.name,
      amount: cents,
      date: draft.date,
      from: draft.from,
      when: draft.when,
      mandatory: draft.mandatory,
    };
    const planned = t('Au rythme prévu');
    const beyond: Impact = {
      text: t(
        "Au-delà des six prochains mois : hors du calcul des quinzaines pour l'instant.",
      ),
      color: theme.pageTextSubdued,
    };
    setImpact([{ text: t('Calcul…'), color: theme.pageTextSubdued }]);
    // Every solve runs in the solver's own Worker: the form keeps answering.
    void (async (): Promise<Impact[]> => {
      const without = withItem(inputs, account, editing, null);
      const before = await solveTask({ kind: 'walk', inputs: without });
      const after = await solveTask({
        kind: 'walk',
        inputs: withItem(inputs, account, editing, item),
      });
      const was = item.date ? stepOf(before, item.date) : undefined;
      const now = item.date ? stepOf(after, item.date) : undefined;
      const change =
        was && now
          ? was.thresholdHoursPerWeek === now.thresholdHoursPerWeek
            ? t('Son minimum reste à {{hours}} par semaine.', {
                hours: hours(now.thresholdHoursPerWeek),
              })
            : t('Son minimum passe de {{before}} à {{after}} par semaine.', {
                before: hours(was.thresholdHoursPerWeek),
                after: hours(now.thresholdHoursPerWeek),
              })
          : '';
      // A pay that already lacked money is not said to stop being enough.
      const tooShort = (step: WalkStep, already: number): Impact => ({
        text:
          already > 0
            ? t(
                'La paie du {{pay}} manque déjà de {{already}} : il lui manquerait {{amount}}.',
                {
                  pay: textDate(step.payday),
                  already: money(already),
                  amount: money(step.shortfall),
                },
              )
            : t(
                'La paie du {{pay}} ne suffirait plus : il manquerait {{amount}}.',
                { pay: textDate(step.payday), amount: money(step.shortfall) },
              ),
        color: theme.errorText,
      });

      if (item.kind === 'entree') {
        return now
          ? [
              {
                text: `${t('Comptée dans la paie du {{pay}}, à partir du {{date}}.', { pay: textDate(now.payday), date: textDate(item.date) })} ${change}`,
                color: theme.noticeTextLight,
              },
            ]
          : [beyond];
      }
      if (item.kind === 'depense') {
        if (!now) {
          return [beyond];
        }
        return [
          now.shortfall > 0
            ? tooShort(now, was?.shortfall ?? 0)
            : {
                text: `${t('Payée par la paie du {{pay}}.', { pay: textDate(now.payday) })} ${change}`,
                color: theme.pageText,
              },
        ];
      }

      // An investment: what the plan does of it, what holding its date
      // takes, and the earliest it can be paid at the most hours.
      const wanted = { name: item.name, amount: item.amount };
      const lines: Impact[] = [];
      const need = after.at(-1)?.needs.find(n => n.id === 'draft');
      if (item.mandatory && item.date) {
        lines.push(
          !now
            ? beyond
            : now.shortfall > 0
              ? tooShort(now, was?.shortfall ?? 0)
              : {
                  text: `${t('Payé à sa date, avant les dettes, par la paie du {{pay}}.', { pay: textDate(now.payday) })} ${change}`,
                  color: theme.noticeTextLight,
                },
        );
      } else if (!item.date) {
        lines.push({
          text: t(
            'Sans date : il sera financé quand les dettes le permettront.',
          ),
          color: theme.pageText,
        });
      } else if (!need) {
        lines.push(beyond);
      } else if (need.paidSoFar >= need.amount) {
        lines.push({
          text: t('Financé pour sa date, une fois les dettes servies.'),
          color: theme.noticeTextLight,
        });
      } else {
        lines.push({
          text: t(
            'Non financé : les dettes passent avant. Il manquerait {{amount}} au {{date}}.',
            {
              amount: money(need.amount - need.paidSoFar),
              date: textDate(item.date),
            },
          ),
          color: theme.warningText,
        });
      }
      lines[0] = { ...lines[0], label: planned };

      let reachable = true;
      if (item.date) {
        const floor = config.pay.minHoursPerWeek;
        const needed = await solveTask({
          kind: 'hoursToFund',
          inputs: without,
          wanted,
          date: item.date,
          maxHours,
        });
        reachable = needed.hours !== null;
        lines.push({
          label:
            item.when === 'period'
              ? t('Pour tenir la période')
              : t('Pour tenir la date'),
          text:
            needed.hours === null
              ? t(
                  'Même à {{max}} par semaine, il manquerait {{amount}} au {{date}}.',
                  {
                    max: hours(maxHours),
                    amount: money(needed.missing),
                    date: textDate(item.date),
                  },
                )
              : needed.hours <= floor
                ? t(
                    'Les heures garanties suffisent ({{hours}} par semaine), en le finançant avant les dettes.',
                    { hours: hours(floor) },
                  )
                : t(
                    "{{hours}} par semaine d'ici le {{date}}, au lieu de {{floor}}, en le finançant avant les dettes.",
                    {
                      hours: hours(needed.hours),
                      date: textDate(item.date),
                      floor: hours(floor),
                    },
                  ),
          color: needed.hours === null ? theme.errorText : theme.pageText,
        });
      }
      if (!item.date || item.when === 'period' || !reachable) {
        const day = await solveTask({
          kind: 'earliestFundingDay',
          inputs: without,
          wanted,
          maxHours,
          from: item.when === 'period' && item.from > today ? item.from : today,
        });
        lines.push({
          label: t('Au plus tôt'),
          text: day
            ? t('Le {{date}}, en travaillant {{max}} par semaine.', {
                date: longDate(day),
                max: hours(maxHours),
              })
            : t(
                'Pas dans les six prochains mois, même à {{max}} par semaine.',
                { max: hours(maxHours) },
              ),
          color: day ? theme.noticeTextLight : theme.warningText,
        });
      }
      return lines;
    })()
      .catch((): Impact[] => [
        {
          text: t("Le calcul de l'effet n'a pas abouti."),
          color: theme.pageTextSubdued,
        },
      ])
      .then(result => {
        if (!cancelled) {
          setImpact(result);
        }
      });
    return () => {
      cancelled = true;
    };
    // The inputs change identity only when the walk does.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, inputs, draft?.tried]);

  // ── Writing ──────────────────────────────────────────────────────────────

  async function createExpense(item: Item) {
    if (!account) {
      throw new Error('no account');
    }
    const payee =
      payees.find(p => p.name.toLowerCase() === item.name.toLowerCase())?.id ??
      (await send('payee-create', { name: item.name }));
    await send('schedule/create', {
      schedule: { name: item.name, posts_transaction: false },
      conditions: [
        { op: 'is', field: 'date', value: item.date },
        { op: 'is', field: 'amount', value: -item.amount },
        { op: 'is', field: 'account', value: account },
        { op: 'is', field: 'payee', value: payee },
      ],
    });
  }

  async function write(item: Item, previous: Item | null) {
    if (!config) {
      return;
    }
    if (item.kind === 'depense') {
      const schedule = schedules.find(s => s.id === previous?.id);
      if (previous && schedule) {
        await send('schedule/update', {
          schedule: { id: schedule.id, name: item.name },
          conditions: [
            { op: 'is', field: 'date', value: item.date },
            { op: 'is', field: 'amount', value: -item.amount },
            { op: 'is', field: 'account', value: schedule._account },
            { op: 'is', field: 'payee', value: schedule._payee },
          ],
          resetNextDate: true,
        });
      } else {
        await createExpense(item);
      }
    } else if (item.kind === 'entree') {
      const income = {
        id: previous?.id ?? uuidv4(),
        name: item.name,
        amount: item.amount,
        date: item.date,
      };
      saveConfig({
        ...config,
        extraIncomes: [
          ...config.extraIncomes.filter(i => i.id !== income.id),
          income,
        ],
      });
    } else {
      const existing = config.projects.find(p => p.id === previous?.id);
      const project = {
        ...existing,
        id: existing?.id ?? (previous?.id || uuidv4()),
        name: item.name,
        amount: item.amount,
        wished: item.date || undefined,
        wishedFrom: item.when === 'period' && item.from ? item.from : undefined,
        deadline: item.date ? DEADLINE_OF[item.when] : ('none' as const),
        mandatory: item.mandatory && item.date !== '',
        priority:
          existing?.priority ??
          config.projects.reduce((max, p) => Math.max(max, p.priority), 0) + 1,
      };
      saveConfig({
        ...config,
        projects: [
          ...config.projects.filter(p => p.id !== project.id),
          project,
        ],
      });
    }
  }

  async function remove(item: Item) {
    if (!config) {
      return;
    }
    if (item.kind === 'depense') {
      await send('schedule/delete', { id: item.id });
    } else if (item.kind === 'entree') {
      saveConfig({
        ...config,
        extraIncomes: config.extraIncomes.filter(i => i.id !== item.id),
      });
    } else {
      saveConfig({
        ...config,
        projects: config.projects.filter(p => p.id !== item.id),
      });
    }
  }

  async function save() {
    if (!draft) {
      return;
    }
    if (!valid || cents === null) {
      setDraft({ ...draft, tried: true });
      return;
    }
    setSaving(true);
    setFailure(null);
    try {
      await write(
        {
          kind: draft.kind,
          id: draft.id ?? '',
          name: draft.name.trim(),
          amount: cents,
          date: isInvestment && draft.when === 'none' ? '' : draft.date,
          from: draft.from,
          when: draft.when,
          mandatory: draft.mandatory,
        },
        editing,
      );
      setNotice({
        text: editing
          ? t('{{kind}} : modification enregistrée', {
              kind: kindLabel[draft.kind],
            })
          : t('{{kind}} : ajout enregistré', { kind: kindLabel[draft.kind] }),
      });
      setDraft(null);
    } catch {
      setFailure(
        t("L'enregistrement a échoué : rien n'a été modifié. Réessayez."),
      );
    }
    setSaving(false);
  }

  async function confirmRemove() {
    if (!editing) {
      return;
    }
    const removed = editing;
    setSaving(true);
    setFailure(null);
    try {
      await remove(removed);
      setNotice({
        text: t('{{kind}} : suppression faite', {
          kind: kindLabel[removed.kind],
        }),
        // Put back exactly as it was.
        undo: () => void write(removed, null).then(() => setNotice(null)),
      });
      setDraft(null);
    } catch {
      setFailure(
        t("La suppression a échoué : rien n'a été supprimé. Réessayez."),
      );
    }
    setConfirming(false);
    setSaving(false);
  }

  function open(item: Item | null) {
    setConfirming(false);
    setFailure(null);
    setDraft(
      item
        ? {
            ...item,
            mode: 'edit',
            amount: (item.amount / 100).toFixed(2).replace('.', ','),
            tried: false,
          }
        : blank('depense'),
    );
  }

  if (!config) {
    return (
      <MtxPage title={t('Prévisions')}>
        {!isLoading && (
          <Section style={{ padding: 16 }}>
            <Text style={{ color: theme.pageTextSubdued }}>
              <Trans>MTXbudget n'est pas encore configuré.</Trans>
            </Text>
          </Section>
        )}
      </MtxPage>
    );
  }

  const nameLabel: Record<Kind, string> = {
    depense: t('Quoi'),
    entree: t("D'où vient-il"),
    investissement: t('Pour quoi'),
  };
  const dateLabel: Record<Kind, string> = {
    depense: t('À payer le'),
    entree: t('Disponible le'),
    investissement: t('Pour le'),
  };
  const filters: [Kind | 'tout', string][] = [
    ['tout', t('Tout')],
    ['depense', t('Dépenses')],
    ['entree', t('Entrées')],
    ['investissement', t('Investissements')],
  ];
  const bottom = isNarrowWidth ? MOBILE_NAV_HEIGHT : 0;
  const showDateError = Boolean(
    draft && dateError && (draft.tried || draft.date !== ''),
  );
  const title =
    draft?.mode === 'edit'
      ? t('Modifier la prévision')
      : t('Nouvelle prévision');

  return (
    <MtxPage title={t('Prévisions')}>
      <View style={{ gap: 12, paddingBottom: 96 }}>
        <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
          <Trans>Ce que vos échéances ne connaissent pas encore</Trans>
        </Text>
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          {filters.map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
              style={{
                ...chipStyle(filter === value, theme.tableBackground),
                padding: '0 14px',
              }}
            >
              {label}
            </button>
          ))}
        </View>

        {items.length === 0 ? (
          <Section
            style={{
              padding: '24px 16px',
              gap: 12,
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Text style={{ fontWeight: 600, color: theme.pageTextDark }}>
              <Trans>Aucune prévision pour l'instant</Trans>
            </Text>
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              <Trans>
                Ajoutez ici une dépense qui n'est pas dans vos échéances, une
                entrée d'argent autre que le salaire, ou un investissement à
                financer pour une date. Le calcul des quinzaines en tient compte
                aussitôt.
              </Trans>
            </Text>
            <Button
              variant="primary"
              onPress={() => open(null)}
              style={{ alignSelf: 'stretch', minHeight: 48, fontWeight: 600 }}
            >
              <Trans>Ajouter une prévision</Trans>
            </Button>
          </Section>
        ) : (
          <>
            {groups.map(pay => {
              const rows = shown.filter(
                i => (i.date ? payBefore(i.date) : '') === pay,
              );
              const total = rows.reduce(
                (sum, i) => sum + (i.kind === 'entree' ? i.amount : -i.amount),
                0,
              );
              return (
                <Section
                  key={pay}
                  title={
                    pay
                      ? t('Paie du {{date}}', { date: textDate(pay) })
                      : t('Sans date')
                  }
                  aside={`${total < 0 ? '−' : '+'} ${money(Math.abs(total))}`}
                >
                  {rows.map((item, index) => {
                    const outcome = fate(item);
                    return (
                      <button
                        key={`${item.kind}-${item.id}`}
                        type="button"
                        aria-label={t('Modifier : {{name}}', {
                          name: item.name,
                        })}
                        onClick={() => open(item)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '10px 16px',
                          border: 0,
                          borderBottom:
                            index === rows.length - 1
                              ? undefined
                              : `1px solid ${theme.tableBorder}`,
                          background: 'none',
                          color: theme.pageText,
                          font: 'inherit',
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            width: 48,
                            flexShrink: 0,
                            fontSize: 12,
                            fontWeight: 600,
                            color: theme.pageTextSubdued,
                          }}
                        >
                          {item.date ? shortDate(item.date) : '—'}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>{item.name}</span>
                          <span
                            style={{
                              fontSize: 12,
                              color: kindColor[item.kind],
                            }}
                          >
                            {rowLabel[item.kind]}
                            {item.mandatory
                              ? ` · ${t('avant les dettes')}`
                              : ''}
                          </span>
                          <span
                            style={{
                              fontSize: 12,
                              color: outcome.color ?? theme.pageTextSubdued,
                            }}
                          >
                            {outcome.text}
                          </span>
                        </span>
                        <span
                          style={{
                            flexShrink: 0,
                            fontWeight: 500,
                            color:
                              item.kind === 'entree'
                                ? theme.noticeTextLight
                                : undefined,
                          }}
                        >
                          {`${item.kind === 'entree' ? '+' : '−'} ${money(item.amount)}`}
                        </span>
                        <SvgCheveronRight
                          width={16}
                          height={16}
                          style={{
                            flexShrink: 0,
                            color: theme.pageTextSubdued,
                          }}
                        />
                      </button>
                    );
                  })}
                </Section>
              );
            })}
            {shown.length === 0 && (
              <Section style={{ padding: 16 }}>
                <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                  <Trans>Rien de ce type pour l'instant.</Trans>
                </Text>
              </Section>
            )}
            <Text
              style={{
                fontSize: 13,
                color: theme.pageTextSubdued,
                padding: '0 4px',
              }}
            >
              <Trans>
                Touchez une ligne pour la modifier ou la supprimer. Vos
                échéances régulières se gèrent dans « Échéances ».
              </Trans>
            </Text>
          </>
        )}
      </View>

      {items.length > 0 && !draft && (
        <button
          type="button"
          aria-label={t('Ajouter une prévision')}
          onClick={() => open(null)}
          style={{
            position: 'fixed',
            right: 16,
            bottom: bottom + 16,
            zIndex: 90,
            minHeight: 56,
            padding: '0 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            border: 0,
            borderRadius: 28,
            backgroundColor: theme.buttonPrimaryBackground,
            color: theme.buttonPrimaryText,
            font: 'inherit',
            fontWeight: 600,
            boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
            cursor: 'pointer',
          }}
        >
          <SvgAdd width={18} height={18} />
          <Trans>Ajouter</Trans>
        </button>
      )}

      {notice && !draft && (
        <View
          role="status"
          style={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: bottom + 88,
            zIndex: 95,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '8px 16px',
            minHeight: 48,
            borderRadius: 8,
            backgroundColor: theme.tableBorder,
          }}
        >
          <Text style={{ fontSize: 13 }}>{notice.text}</Text>
          {notice.undo && (
            <Button
              variant="bare"
              onPress={notice.undo}
              style={{
                minHeight: 44,
                fontWeight: 600,
                color: theme.mobileNavItemSelected,
              }}
            >
              <Trans>Annuler</Trans>
            </Button>
          )}
        </View>
      )}

      {draft && (
        <View
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            backgroundColor: 'rgba(0,0,0,0.72)',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <View
            role="dialog"
            aria-label={title}
            style={{
              width: '100%',
              maxWidth: 520,
              maxHeight: 'calc(100% - 40px)',
              overflowY: 'auto',
              gap: 14,
              padding: '8px 16px 20px',
              backgroundColor: theme.tableBackground,
              borderTop: `1px solid ${theme.tableBorder}`,
              borderRadius: '16px 16px 0 0',
            }}
          >
            <View
              style={{
                alignSelf: 'center',
                width: 36,
                height: 4,
                flexShrink: 0,
                borderRadius: 2,
                backgroundColor: theme.pageTextSubdued,
              }}
            />
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: theme.pageTextDark,
                }}
              >
                {title}
              </Text>
              <Button
                variant="bare"
                aria-label={t('Fermer')}
                onPress={() => setDraft(null)}
                style={{ width: 44, height: 44, padding: 0 }}
              >
                <SvgClose width={18} height={18} />
              </Button>
            </View>

            <View
              style={{
                flexDirection: 'row',
                flexShrink: 0,
                gap: 4,
                padding: 4,
                borderRadius: 6,
                border: `1px solid ${theme.tableBorder}`,
                backgroundColor: theme.pageBackground,
              }}
            >
              {KINDS.map(kind => (
                <button
                  key={kind}
                  type="button"
                  aria-pressed={draft.kind === kind}
                  disabled={draft.mode === 'edit'}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      kind,
                      mandatory: false,
                      from: '',
                      when: kind === 'investissement' ? 'none' : 'date',
                      date:
                        kind === 'investissement' ? '' : draft.date || today,
                    })
                  }
                  style={{
                    flex: 1,
                    minHeight: 44,
                    border: 0,
                    borderRadius: 4,
                    backgroundColor:
                      draft.kind === kind
                        ? theme.buttonPrimaryBackground
                        : 'transparent',
                    color:
                      draft.kind === kind
                        ? theme.buttonPrimaryText
                        : theme.pageTextSubdued,
                    font: 'inherit',
                    fontSize: 13,
                    fontWeight: draft.kind === kind ? 600 : 500,
                    cursor: draft.mode === 'edit' ? 'default' : 'pointer',
                  }}
                >
                  {kindLabel[kind]}
                </button>
              ))}
            </View>

            <Field label={t('Montant')}>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  minHeight: 64,
                  padding: '0 12px',
                  borderRadius: 4,
                  border: `1px solid ${draft.tried && cents === null ? theme.errorText : theme.tableBorder}`,
                  backgroundColor: theme.pageBackground,
                }}
              >
                <input
                  aria-label={t('Montant')}
                  inputMode="decimal"
                  autoFocus={draft.mode === 'add'}
                  value={draft.amount}
                  placeholder="0,00"
                  onChange={event =>
                    setDraft({ ...draft, amount: event.target.value })
                  }
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 0,
                    outline: 'none',
                    background: 'transparent',
                    color: theme.pageTextDark,
                    font: 'inherit',
                    fontSize: 36,
                    fontWeight: 600,
                    textAlign: 'right',
                    padding: '12px 0',
                  }}
                />
                <span style={{ fontSize: 20 }}>$</span>
              </span>
            </Field>

            <Field
              label={nameLabel[draft.kind]}
              error={
                (draft.tried || nameTaken) && nameError ? nameError : undefined
              }
            >
              <input
                aria-label={nameLabel[draft.kind]}
                value={draft.name}
                onChange={event =>
                  setDraft({ ...draft, name: event.target.value })
                }
                style={inputStyle(
                  Boolean((draft.tried || nameTaken) && nameError),
                )}
              />
            </Field>

            {isInvestment ? (
              <View style={{ gap: 8, flexShrink: 0 }}>
                <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                  <Trans>Pour quand</Trans>
                </Text>
                <View
                  style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}
                >
                  {WHENS.map(when => (
                    <button
                      key={when}
                      type="button"
                      aria-pressed={draft.when === when}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          when,
                          date:
                            when === 'none'
                              ? ''
                              : when === 'date' || when === 'period'
                                ? draft.date
                                : dateOf(when, today),
                          from: when === 'period' ? draft.from || today : '',
                          mandatory: when === 'none' ? false : draft.mandatory,
                        })
                      }
                      style={chipStyle(
                        draft.when === when,
                        theme.pageBackground,
                      )}
                    >
                      {whenLabel[when]}
                    </button>
                  ))}
                </View>
                {(draft.when === 'date' || draft.when === 'period') && (
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    {draft.when === 'period' && (
                      <Field label={t('Du')}>
                        <input
                          aria-label={t('Du')}
                          type="date"
                          value={draft.from}
                          onChange={event =>
                            setDraft({ ...draft, from: event.target.value })
                          }
                          style={inputStyle(false)}
                        />
                      </Field>
                    )}
                    <Field
                      label={draft.when === 'period' ? t('Au') : t('Pour le')}
                    >
                      <input
                        aria-label={
                          draft.when === 'period' ? t('Au') : t('Pour le')
                        }
                        type="date"
                        min={today}
                        value={draft.date}
                        onChange={event =>
                          setDraft({ ...draft, date: event.target.value })
                        }
                        style={inputStyle(showDateError)}
                      />
                    </Field>
                  </View>
                )}
                <Text
                  style={{
                    fontSize: 12,
                    color: showDateError
                      ? theme.errorText
                      : theme.pageTextSubdued,
                  }}
                >
                  {showDateError
                    ? dateError
                    : draft.when === 'none'
                      ? t(
                          'Aucune date : il passera quand les dettes le permettront.',
                        )
                      : draft.date === ''
                        ? t('Choisissez une date.')
                        : draft.when === 'period' && draft.from
                          ? t('Entre le {{from}} et le {{to}}', {
                              from: longDate(draft.from),
                              to: longDate(draft.date),
                            })
                          : t('Au plus tard le {{date}}', {
                              date: longDate(draft.date),
                            })}
                </Text>

                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 4,
                    border: `1px solid ${theme.tableBorder}`,
                    opacity: draft.when === 'none' ? 0.5 : 1,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontWeight: 500 }}>
                      <Trans>Le financer avant les dettes</Trans>
                    </Text>
                    <Text
                      style={{ fontSize: 12, color: theme.pageTextSubdued }}
                    >
                      <Trans>
                        Il devient une dépense à payer à sa date : le minimum
                        d'heures des paies en tient compte.
                      </Trans>
                    </Text>
                  </View>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={draft.mandatory}
                    aria-label={t('Le financer avant les dettes')}
                    disabled={draft.when === 'none'}
                    onClick={() =>
                      setDraft({ ...draft, mandatory: !draft.mandatory })
                    }
                    style={{
                      position: 'relative',
                      flexShrink: 0,
                      width: 44,
                      height: 26,
                      padding: 0,
                      borderRadius: 13,
                      border: draft.mandatory
                        ? 0
                        : `1px solid ${theme.tableBorder}`,
                      backgroundColor: draft.mandatory
                        ? theme.buttonPrimaryBackground
                        : theme.tableHeaderBackground,
                      cursor: draft.when === 'none' ? 'default' : 'pointer',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: draft.mandatory ? 3 : 2,
                        left: draft.mandatory ? 21 : 2,
                        width: 20,
                        height: 20,
                        borderRadius: 10,
                        backgroundColor: draft.mandatory
                          ? theme.buttonPrimaryText
                          : theme.pageTextSubdued,
                      }}
                    />
                  </button>
                </View>
              </View>
            ) : (
              <Field
                label={dateLabel[draft.kind]}
                hint={draft.date ? longDate(draft.date) : undefined}
                error={showDateError ? (dateError ?? undefined) : undefined}
              >
                <input
                  aria-label={dateLabel[draft.kind]}
                  type="date"
                  min={draft.kind === 'entree' ? undefined : today}
                  value={draft.date}
                  onChange={event =>
                    setDraft({ ...draft, date: event.target.value })
                  }
                  style={inputStyle(showDateError)}
                />
              </Field>
            )}

            <View
              style={{
                gap: 8,
                flexShrink: 0,
                padding: '10px 12px',
                borderRadius: 4,
                border: `1px solid ${theme.tableBorder}`,
                backgroundColor: theme.tableHeaderBackground,
              }}
            >
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                <Trans>Ce que ça change</Trans>
              </Text>
              {(impact.length > 0
                ? impact
                : [{ text: t('Calcul…'), color: theme.pageTextSubdued }]
              ).map((line: Impact) => (
                <View key={`${line.label ?? ''}${line.text}`}>
                  {line.label && (
                    <Text
                      style={{ fontSize: 12, color: theme.pageTextSubdued }}
                    >
                      {line.label}
                    </Text>
                  )}
                  <Text style={{ fontSize: 13, color: line.color }}>
                    {line.text}
                  </Text>
                </View>
              ))}
            </View>

            {failure && (
              <Text
                role="alert"
                style={{ fontSize: 13, flexShrink: 0, color: theme.errorText }}
              >
                {failure}
              </Text>
            )}

            {confirming && editing ? (
              <View
                role="alertdialog"
                aria-label={t('Supprimer la prévision')}
                style={{
                  gap: 12,
                  flexShrink: 0,
                  padding: 16,
                  borderRadius: 8,
                  border: `1px solid ${theme.tableBorder}`,
                  backgroundColor: theme.tableHeaderBackground,
                }}
              >
                <Text
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: theme.pageTextDark,
                  }}
                >
                  {t('Supprimer « {{name}} » ?', { name: editing.name })}
                </Text>
                <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                  {t(
                    'Le montant de {{amount}} ne sera plus compté dans le calcul des quinzaines.',
                    { amount: money(editing.amount) },
                  )}
                </Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Button
                    isDisabled={saving}
                    onPress={() => setConfirming(false)}
                    style={{ flex: 1, minHeight: 48 }}
                  >
                    <Trans>Annuler</Trans>
                  </Button>
                  <Button
                    isDisabled={saving}
                    onPress={() => void confirmRemove()}
                    style={{
                      flex: 1,
                      minHeight: 48,
                      fontWeight: 600,
                      border: 0,
                      backgroundColor: '#b3261e',
                      color: '#ffffff',
                    }}
                  >
                    {saving ? t('Suppression…') : t('Supprimer')}
                  </Button>
                </View>
              </View>
            ) : (
              <>
                <Button
                  variant="primary"
                  isDisabled={saving}
                  onPress={() => void save()}
                  style={{ minHeight: 52, flexShrink: 0, fontWeight: 600 }}
                >
                  {saving
                    ? t('Enregistrement…')
                    : draft.mode === 'edit'
                      ? t('Enregistrer les changements')
                      : t('Enregistrer')}
                </Button>
                {draft.mode === 'edit' && (
                  <Button
                    isDisabled={saving}
                    onPress={() => setConfirming(true)}
                    style={{
                      minHeight: 48,
                      flexShrink: 0,
                      color: theme.errorText,
                    }}
                  >
                    <Trans>Supprimer cette prévision</Trans>
                  </Button>
                )}
              </>
            )}
          </View>
        </View>
      )}
    </MtxPage>
  );
}
